import ActionButton from 'components/ActionButton';
import AdjustInventoryItemQuantityModal from 'components/AdjustInventoryItemQuantityModal';
import BackButton from 'components/BackButton';
import InfoModal from 'components/InfoModal';
import { Loading } from 'components/loading/Loading';
import ViewImageModal from 'components/ViewItemImageModal';
import React, { useEffect, useState } from 'react';
import {
  deleteInventoryItem,
  fetchInventoryItemDetails,
  toggleActiveStatusForInventoryItem,
  toggleHomeBoxInclusion,
  updateInventoryItem,
  updateInventoryItemImage,
} from 'services/api/inventoryItems';
import { MODAL_TYPES, showError, showSuccess } from 'utils';
import ComparableItems from './ComparableItems';
import styles from './index.module.scss';
import ItemHistoryLogs from './ItemHistoryLogs';
import RelatedItems from './RelatedItems';
import SupplierDetails from './SupplierDetails';
import _ from 'lodash';
import Currency from 'components/Currency/Currency';
import ItemKeywords from './ItemKeywords';
import ItemChannels from './ItemChannels';
import CustomLink from 'components/TableLink';
import ItemBasicDetails from './ItemBasicDetails';
import SEOFields from './SEOFields';
import CalculatedItems from './CalculatedItems';
import InventoryItemTags from './InventoryItemTags';
import ItemBlogs from './Blogs';
import ActiveStatusHistory from './activeStatusHistory';
import UserDetails from './UserDetails';
import AdjustItemUnfulfilledQuantityModal from 'components/AdjustUnfulfilledItemQuantityModal';
import { connect } from 'react-redux';
import { mapDispatchToProps as authActions } from 'actions/auth';
import SoldHistory from './soldHistory';

const mapStateToProps = (state: any) => ({
  isAuthenticated: !!state.auth.loggedInUser,
  userRoles: state.auth.loggedInUser ? state.auth.loggedInUser.get('roles') : [],
});

const InventoryItemDetailsComponent = props => {
  const { userRoles, match: { params: { itemId }}} = props;
  const [loading, setLoading] = useState<boolean>(true);
  const [itemDetails, setItemDetails] = useState<Record<string, any>>({});
  const [editBasicInfo, setEditBasicInfo] = useState<boolean>(false);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [restoreLoading, setRestoreLoading] = useState<boolean>(false);
  const [activeLoading, setActiveLoading] = useState<boolean>(false);
  const [excludedLoading, setExcludedLoading] = useState<boolean>(false);
  const [addSupplierModalOpen, setAddSupplierModalOpen] = useState<boolean>(false);
  const [viewImageModalOpen, setViewImageModalOpen] = useState<boolean>(false);
  const [adjustQuantityModalOpen, setAdjustQuantityModalOpen] = useState<boolean>(false);
  const [adjustUnfulfilledQuantityModalOpen, setAdjustUnfulfilledQuantityModalOpen] = useState<boolean>(false);
  const [relatedItemsModalOpen, setRelatedItemsModalOpen] = useState<boolean>(false);
  const [updateSupplierModalOpen, setUpdateSupplierModalOpen] = useState<boolean>(false);
  const [comparableItemsModalOpen, setComparableItemsModalOpen] = useState<boolean>(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);
  const [updatedItem, setUpdatedItem] = useState<Record<string, any>>({});
  const [itemHasParentItem, setItemHasParentItem] = useState<boolean>(false);
  const [channels, setChannels] = useState<Record<string, any>[]>([]);

  const fetchInventoryItemDetailsFn = async () => {
    try {
      const response = await fetchInventoryItemDetails(itemId);
      setItemDetails(response);
      setUpdatedItem(response);
      setItemHasParentItem(!!_.keys(response.parentItem).length);
      setChannels(response.channels);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItemDetailsFn();
  }, []);

  const toggleModal = () => setAddSupplierModalOpen(!addSupplierModalOpen);
  const toggleUpdateModal = () => setUpdateSupplierModalOpen(!updateSupplierModalOpen);

  const updateInventoryItemDetails = async () => {
    setDetailsLoading(true);
    const inventoryAddress = _.mapValues(
      _.mapKeys(
        _.pick(updatedItem.inventoryAddress, ['start', 'end']),
        (_, key) => `${key}InventoryAddress`,
      ),
      obj => _.pick(obj, ['furnitureNo', 'shelfNo', 'storageSection']),
    );
    const data = {
      id: itemId,
      name: updatedItem.name,
      englishName: updatedItem.enName,
      description: updatedItem.description,
      englishDescription: updatedItem.enDescription,
      unitId: _.get(updatedItem, 'unit.objectId', _.get(updatedItem, 'unit', '')),
      categoryId: updatedItem.category.objectId,
      probability: updatedItem.probability || 0,
      price: updatedItem.price || 0,
      unfulfilledStock: updatedItem.unfulfilledStock || 0,
      origin: updatedItem.origin ? updatedItem.origin : undefined,
      isPromotional: updatedItem.isPromotional,
      isFeatured: updatedItem.isFeatured,
      mustHave: updatedItem.mustHave,
      packagingType: updatedItem.packagingType,
      weightedAvgCost: updatedItem.weightedAvgCost,
      automaticReorder: updatedItem.automaticReorder,
      reorderThreshold: updatedItem.reorderThreshold || 0,
      reorderQuantity: updatedItem.reorderQuantity || 0,
      ...inventoryAddress,
    };
    try {
      await updateInventoryItem(data);
      await fetchInventoryItemDetailsFn();
      showSuccess('Details updated sucessfully.');
      setEditBasicInfo(false);
    } catch (e) {
      showError(e);
    } finally {
      setDetailsLoading(false);
    }
  };

  const toggleInventoryItemActive = async () => {
    setActiveLoading(true);
    try {
      await toggleActiveStatusForInventoryItem(itemId);
      itemDetails.active = !itemDetails.active;
      setItemDetails(itemDetails);
      showSuccess(`Item marked as ${itemDetails.active ? 'active' : 'inactive'} successfully.`);
    } catch (e) {
      showError(e);
    } finally {
      setActiveLoading(false);
    }
  };

  const toggleHomeBoxInclusionFn = async () => {
    setExcludedLoading(true);
    try {
      await toggleHomeBoxInclusion(itemId);
      itemDetails.excludedFromHomeBox = !itemDetails.excludedFromHomeBox;
      setItemDetails(itemDetails);
      showSuccess(`Item ${itemDetails.excludedFromHomeBox ? 'excluded from' : 'included in'} Home Box.`);
    } catch (e) {
      showError(e);
    } finally {
      setExcludedLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteInventoryItem(itemId);
      showSuccess('Inventory Item deleted successfully');
      await fetchInventoryItemDetailsFn();
      setConfirmDeleteModalOpen(false);
    } catch (e) {
      showError(e);
    }
  };

  const handleRestore = async () => {
    try {
      setRestoreLoading(true);
      await updateInventoryItem({ id: itemId, deleted: false });
      await fetchInventoryItemDetailsFn();
      showSuccess('Inventory Item restored successfully');
    } catch (e) {
      showError(e);
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleEditCancel = (item: Record<string, any>) => {
    setEditBasicInfo(false);
    setUpdatedItem({
      ...itemDetails,
      ...item,
    });
  };


  const getParentItemDetails = () => {
    const { parentItem: { objectId, name, image, price }, multiplier = '-' } = itemDetails;
    return (
      <div className={`${styles['parent-item-section']} inventory-items-section`}>
        <div className={styles['section-header']}>
          <div className={styles['section-heading']}>Parent Item</div>
        </div>
        <div className={styles['details-section']}>
          <div className={styles['details-group']}>
            <div className={styles['item-image']}>
              {/* eslint-disable-next-line @typescript-eslint/no-var-requires */}
              <img src={_.get(image, 'url', require('assets/product-placeholder.png'))} />
            </div>
          </div>
          <div>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Item ID</div>
              <div className={styles['info']}>
                <CustomLink
                  text={objectId}
                  linkTo={`/inventory-items/${objectId}`}
                />
              </div>
            </div>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Name</div>
              <div className={styles['info']}>{name}</div>
            </div>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Price</div>
              <div className={styles['info']}><Currency amount={price} /></div>
            </div>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Multiplier</div>
              <div className={styles['info']}>{multiplier}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getDeleteRestoreButton = () => {
    if (itemDetails?.deleted) {
      return (
        <div className={`d-flex align-items-center ${styles['restore-button']}`}>
          <span className={styles['delete-label-text']}>Deleted</span>
          <ActionButton
            text={'Restore'}
            active={!restoreLoading}
            isLoading={restoreLoading}
            onClick={handleRestore}
            customClass={''}
          />
        </div>
      );
    } else {
      return (
        <ActionButton
          text={'Delete'}
          active={true}
          isLoading={false}
          onClick={() => setConfirmDeleteModalOpen(true)}
          customClass={styles['delete-button']}
        />
      );
    }
  };

  const getHeader = () => (
    <div className={styles['header']}>
      <div className={'d-flex flex-column justify-content-between'}>
        <div className={styles['heading']}>Inventory Item Details</div>
        <BackButton />
      </div>
      <div className={'d-flex'}>
        {getDeleteRestoreButton()}
      </div>
    </div>
  );

  const getModals = () => {
    if (viewImageModalOpen) return (
      <ViewImageModal
        originalAspect={false}
        source={itemDetails.image?.url}
        toggleModal={() => setViewImageModalOpen(!viewImageModalOpen)}
      />
    );
    if (adjustQuantityModalOpen) return (
      <AdjustInventoryItemQuantityModal
        itemId={itemId}
        itemQuantity={itemDetails.stock}
        toggleModal={() => setAdjustQuantityModalOpen(!adjustQuantityModalOpen)}
        onSuccess={fetchInventoryItemDetailsFn}
      />
    );
    if (adjustUnfulfilledQuantityModalOpen) return (
      <AdjustItemUnfulfilledQuantityModal
        itemId={itemId}
        itemQuantity={itemDetails.unfulfilledStock}
        toggleModal={() => setAdjustUnfulfilledQuantityModalOpen(!adjustUnfulfilledQuantityModalOpen)}
        onSuccess={fetchInventoryItemDetailsFn}
      />
    );
    if (confirmDeleteModalOpen) return (
      <InfoModal
        cancellable={true}
        type={MODAL_TYPES.CONFIRM_CHANGES}
        title={'Inventory Item'}
        message={'Delete the inventory item?'}
        toggleModal={() => setConfirmDeleteModalOpen(!confirmDeleteModalOpen)}
        primaryButtonText={'Delete'}
        primaryButtonOnClick={handleDelete}
        primaryButtonClass={styles['delete-button']}
        secondaryButtonText={'Cancel'}
        secondaryButtonOnClick={() => {
          setConfirmDeleteModalOpen(false);
        }}
        secondaryButtonClass={''}
      />
    );
    return null;
  };

  if (loading) return <Loading />;

  return (
    <>
      {getModals()}
      <div className={`${styles['main-container']}`}>
        {getHeader()}
        <ItemBasicDetails
          activeLoading={activeLoading}
          editInfo={editBasicInfo}
          excludedLoading={excludedLoading}
          detailsLoading={detailsLoading}
          handleEditCancel={handleEditCancel}
          itemDetails={itemDetails}
          itemId={itemId}
          setAdjustQuantityModalOpen={setAdjustQuantityModalOpen}
          setAdjustUnfulfilledQuantityModalOpen={setAdjustUnfulfilledQuantityModalOpen}
          setEditInfo={setEditBasicInfo}
          setUpdatedItem={setUpdatedItem}
          setViewImageModalOpen={setViewImageModalOpen}
          toggleHomeBoxInclusionFn={toggleHomeBoxInclusionFn}
          toggleInventoryItemActive={toggleInventoryItemActive}
          updatedItem={updatedItem}
          updateInventoryItemDetails={updateInventoryItemDetails}
          updateInventoryItemImage={updateInventoryItemImage}
          viewImageModalOpen={viewImageModalOpen}
          userRoles={userRoles} 
        />
        <SEOFields
          loading={detailsLoading}
          handleEditCancel={handleEditCancel}
          inventoryItem={itemDetails}
          setUpdatedItem={setUpdatedItem}
          updatedItem={updatedItem}
          updateInventoryItemDetails={updateInventoryItemDetails}
          userRoles={userRoles} 
        />
        <SoldHistory 
          soldHistory={itemDetails?.soldHistory} 
          nextPredictedSellquantity={itemDetails?.nextPredictedSellquantity}
        />
        <ItemChannels
          itemId={itemId}
          channels={channels}
          setChannels={setChannels}
          userRoles={userRoles} 
        />
        <ItemKeywords itemId={itemId} userRoles={userRoles} />

        <ItemBlogs
          blogs={updatedItem.blogs || []}
          itemId={itemId}
          onSuccess={val => {
            setUpdatedItem(val);
          }}
          userRoles={userRoles} 
        />

        {itemHasParentItem ? getParentItemDetails() : null}
        {/* <ItemLocales
          itemDetails={itemDetails}
          inventoryItemId={itemId}
          toggleAddLocaleModal={toggleAddLocaleModal}
          toggleUpdateLocaleModal={toggleUpdateLocaleModal}
          addModalOpen={addLocaleModalOpen}
          updateModalOpen={updateLocaleModalOpen}
          fetchInventoryItemDetails={fetchInventoryItemDetailsFn}
        /> */}
        {!itemHasParentItem && (
          <SupplierDetails
            {...props}
            inventoryItemId={itemId}
            addSupplierModalOpen={addSupplierModalOpen}
            updateSupplierModalOpen={updateSupplierModalOpen}
            toggleModal={toggleModal}
            toggleUpdateModal={toggleUpdateModal}
            userRoles={userRoles} 
          />
        )}
        <UserDetails 
          inventoryItemId={itemId} 
          userRoles={userRoles} 
        />
        {!itemHasParentItem && (
          <>
            <ItemHistoryLogs itemId={itemDetails?.objectId} />
            <ItemHistoryLogs itemId={itemDetails?.objectId} isUnfulfilledQuantityChangeTable={false} showItemHistoryChart={false} />
          </>
        )}
        <RelatedItems
          itemId={itemDetails.objectId}
          modalOpen={relatedItemsModalOpen}
          toggleModal={() => setRelatedItemsModalOpen(!relatedItemsModalOpen)}
          userRoles={userRoles} 
        />
        <ActiveStatusHistory itemId={itemDetails.objectId} />
        <ComparableItems
          itemId={itemId}
          comparableItems={itemDetails.comparableItems}
          addModalOpen={comparableItemsModalOpen}
          toggleAddModal={() => setComparableItemsModalOpen(!comparableItemsModalOpen)}
          refetchComparableItems={fetchInventoryItemDetailsFn}
          userRoles={userRoles} 
        />
        <CalculatedItems
          itemDetails={itemDetails}
          userRoles={userRoles} 
        />
        <InventoryItemTags 
          itemId={itemDetails.objectId} 
          userRoles={userRoles} 
        />
      </div>
    </>
  );
};

const InventoryItemDetails = connect(mapStateToProps, { ...authActions })(InventoryItemDetailsComponent);

export { InventoryItemDetails };
